/**
 * Beta feature registry.
 *
 * A beta feature is one that ships in the app but is still moving: users opt
 * in (or opt out) per feature under Settings → Beta Features, and every
 * discoverable entry point for a disabled feature is removed from the UI.
 *
 * Two default policies live side by side, and the difference is deliberate:
 *  - `defaultEnabled: true`  — features that already shipped carrying a "Beta"
 *    badge. They stay on for everyone who has not said otherwise; the setting
 *    exists so a user who does not want them can take them out of the way.
 *  - `defaultEnabled: false` — features introduced in this release. They are
 *    invisible until the user ticks the box, so nobody meets them by accident.
 *  A new feature moves from off to on once it is ready for everyone (the
 *  Visual Query Editor did in 1.5.3); a user's explicit choice — stored as an
 *  override — always wins over the default.
 *
 * The pattern mirrors `src/core/licensing/types.ts` (`ProFeature` +
 * `PRO_FEATURES`): a string-literal union plus a metadata record, so the
 * Settings panel is generated from the registry and can never list a feature
 * no gate reads.
 *
 * This module is intentionally dependency-free (no store, no React) so both
 * the persisted store and the pure toolbar/command models can import it.
 */

/**
 * Stable, persisted identity. Never rename a shipped id — it is a key in
 * `appStore.betaFeatures`, and renaming one silently resets that user's choice.
 */
export type BetaFeatureId =
  // Still in Beta, on by default since 1.5.3.
  | 'visual-query-editor'
  // New — default off.
  | 'dashboards'
  // Previously badged "Beta" in-app — default on.
  | 'database-blueprint'
  | 'cross-engine-migrate'
  | 'data-import'
  | 'pl-sql-debugger'
  | 'job-scheduler'
  | 'package-explorer';

export type BetaFeatureInfo = {
  /** Name as the user knows the feature (matches its toolbar / docs wording). */
  name: string;
  /** One or two sentences: what it does, and what turning it off removes. */
  description: string;
  /**
   * Value used when the user has never touched this feature's checkbox.
   * Reading it through `isBetaFeatureEnabled` (rather than seeding the store
   * with it) means a later change of default reaches every user who never
   * expressed a preference.
   */
  defaultEnabled: boolean;
  /** Absolute jamsql.com docs URL, when a page covers the feature. */
  docsUrl?: string;
};

/**
 * Display order in the Settings panel is the order of this record: the
 * new-in-this-release features first, then the long-standing ones.
 */
export const BETA_FEATURES: Record<BetaFeatureId, BetaFeatureInfo> = {
  'visual-query-editor': {
    name: 'Visual Query Editor',
    description:
      'Build and edit SELECT/DML statements visually — the SQL | Visual switch on the Query Editor toolbar, More → Visual Query Editor, Cmd/Ctrl+Shift+V, and the pencil affordance that opens the compact editor at your cursor. Also covers the Visual DDL Editor: the same pencil on CREATE/ALTER statements.',
    defaultEnabled: true,
    docsUrl: 'https://jamsql.com/docs/visual-query-editor/',
  },
  dashboards: {
    name: 'Dashboards',
    description:
      'Multi-tile dashboards (charts, stats, tables, pivots, maps) over your saved connections, plus the Pin to dashboard action in query results, Table Explorer, charts, and Session Browser.',
    defaultEnabled: false,
    docsUrl: 'https://jamsql.com/docs/dashboards/',
  },
  'database-blueprint': {
    name: 'Database Blueprint',
    description:
      'Export a database to a versionable folder of .sql files, then diff, sync, and commit it like source code.',
    defaultEnabled: true,
    docsUrl: 'https://jamsql.com/docs/database-blueprint/',
  },
  'cross-engine-migrate': {
    name: 'Cross-Engine Migration',
    description:
      'Copy a table or a whole database to a connection on a different engine, translating types and data as it goes.',
    defaultEnabled: true,
    docsUrl: 'https://jamsql.com/docs/cross-engine-database-migration/',
  },
  'data-import': {
    name: 'Data Import',
    description:
      'The import wizard for CSV, JSON, Excel, and Parquet files, including the File → Import Data menu item and the Import Data context-menu actions.',
    defaultEnabled: true,
    docsUrl: 'https://jamsql.com/docs/import-data/',
  },
  'pl-sql-debugger': {
    name: 'PL/SQL Debugger',
    description:
      'Step through Oracle PL/SQL packages, procedures, functions, and triggers with breakpoints, watches, and a call stack.',
    defaultEnabled: true,
    docsUrl: 'https://jamsql.com/docs/plsql-debugger/',
  },
  'job-scheduler': {
    name: 'Agent Jobs',
    description:
      'Browse, edit, run, and schedule jobs on SQL Server Agent, pgAgent, the MySQL event scheduler, and Oracle DBMS_SCHEDULER.',
    defaultEnabled: true,
    docsUrl: 'https://jamsql.com/docs/agent-jobs/',
  },
  'package-explorer': {
    name: 'Package Explorer',
    description:
      'Browse an Oracle package specification and body side by side, with per-subprogram navigation.',
    defaultEnabled: true,
    docsUrl: 'https://jamsql.com/docs/package-explorer/',
  },
};

/** Registry order — the Settings panel and any parity test read this. */
export const BETA_FEATURE_IDS = Object.keys(BETA_FEATURES) as BetaFeatureId[];

/**
 * The user's explicit choices only. An id absent from the map means "never
 * decided", which resolves to the registry default — see `resolveBetaFeature`.
 */
export type BetaFeatureOverrides = Partial<Record<BetaFeatureId, boolean>>;

export function resolveBetaFeature(
  overrides: BetaFeatureOverrides | undefined,
  id: BetaFeatureId,
): boolean {
  return overrides?.[id] ?? BETA_FEATURES[id].defaultEnabled;
}

/** Every feature resolved at once — the shape a gate that reads several wants. */
export function resolveAllBetaFeatures(
  overrides: BetaFeatureOverrides | undefined,
): Record<BetaFeatureId, boolean> {
  return Object.fromEntries(
    BETA_FEATURE_IDS.map((id) => [id, resolveBetaFeature(overrides, id)]),
  ) as Record<BetaFeatureId, boolean>;
}

/**
 * Every feature ON, regardless of its default. For the gate-blind surfaces —
 * the Settings toolbar catalogue, which must list every item, and the test
 * fixtures, which must see every entry point.
 */
export function allBetaFeaturesEnabled(): Record<BetaFeatureId, boolean> {
  return Object.fromEntries(BETA_FEATURE_IDS.map((id) => [id, true])) as Record<
    BetaFeatureId,
    boolean
  >;
}

/**
 * Drops unknown keys and non-boolean values from a persisted / imported
 * payload. A settings bundle can arrive from another machine running a
 * different app version, so an id it carries may no longer exist here.
 */
export function normalizeBetaFeatureOverrides(value: unknown): BetaFeatureOverrides {
  if (!value || typeof value !== 'object') return {};
  const out: BetaFeatureOverrides = {};
  for (const id of BETA_FEATURE_IDS) {
    const entry = (value as Record<string, unknown>)[id];
    if (typeof entry === 'boolean') out[id] = entry;
  }
  return out;
}
